import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'The Ecology of Daily Life',
    content: [
      '<strong>Learning Goal:</strong> Analyze your environment\'s impact on stress, focus, and rest, and discover ways to improve personal spaces for better health.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• The ecological model of well-being',
      '• Understanding safety and comfort as foundations',
      '• Psychological effects of clutter, noise, and pollution',
      '• Environmental inequalities and socioeconomic factors',
      '<br/>',
      '<strong>Discussion:</strong> This module dives deeper into how everyday environmental conditions – from air quality to safety – directly impact our well-being. Your daily surroundings create the context for everything you do.'
    ]
  },
  {
    title: 'Safety and Comfort as Foundations',
    content: [
      'Safety and comfort are fundamental to environmental well-being. When these basic needs are met, you can focus on growth rather than survival.',
      '<br/>',
      '<strong>Safety Components:</strong>',
      '• <strong>Physical Safety:</strong> Protection from harm, accidents, and environmental hazards',
      '• <strong>Psychological Safety:</strong> Feeling secure and free from threat',
      '• <strong>Social Safety:</strong> Trust in your community and relationships',
      '• <strong>Economic Safety:</strong> Financial stability to maintain your environment',
      '<br/>',
      '<strong>Comfort Factors:</strong>',
      '• <strong>Temperature Control:</strong> Appropriate heating/cooling for health and productivity',
      '• <strong>Noise Management:</strong> Control over sound levels that affect concentration and rest',
      '• <strong>Space Organization:</strong> Adequate room for movement and storage',
      '• <strong>Lighting Quality:</strong> Natural and artificial light that supports circadian rhythms',
      '<br/>',
      '<strong>The Stress Connection:</strong> Unsafe or uncomfortable environments trigger the body\'s stress response, leading to elevated cortisol levels, poor sleep, and reduced immune function.'
    ]
  },
  {
    title: 'Psychological Effects of Environmental Stressors',
    content: [
      'Environmental stressors can have profound psychological impacts, often operating below conscious awareness.',
      '<br/>',
      '<strong>Noise Pollution Effects:</strong>',
      '• Disrupts concentration and cognitive performance',
      '• Increases stress hormones and heart rate',
      '• Impairs sleep quality and duration',
      '• Contributes to irritability and mood disturbances',
      '<br/>',
      '<strong>Clutter and Disorder:</strong>',
      '• Creates mental fatigue and decision paralysis',
      '• Reduces ability to focus and be productive',
      '• Increases anxiety and feelings of overwhelm',
      '• Affects self-esteem and sense of control',
      '<br/>',
      '<strong>Pollution and Air Quality:</strong>',
      '• Contributes to depression and anxiety',
      '• Impairs cognitive function and memory',
      '• Reduces motivation and energy levels',
      '• Affects sleep quality and mood regulation',
      '<br/>',
      '<strong>The Cumulative Effect:</strong> Multiple environmental stressors compound each other. Poor air quality plus noise plus clutter creates a perfect storm for mental health challenges.'
    ]
  },
  {
    title: 'Environmental Inequalities',
    content: [
      'Environmental quality often correlates with socioeconomic status, creating inequalities in well-being opportunities.',
      '<br/>',
      '<strong>Socioeconomic Factors:</strong>',
      '• <strong>Income Level:</strong> Higher income enables better housing, cleaner neighborhoods, and access to services',
      '• <strong>Education:</strong> Knowledge about environmental health and access to information',
      '• <strong>Occupation:</strong> Job quality affects work environment and economic stability',
      '• <strong>Geography:</strong> Urban vs. rural location impacts access to services and environmental quality',
      '<br/>',
      '<strong>Health Disparities:</strong>',
      '• Lower-income communities often face higher pollution levels',
      '• Poor neighborhoods have fewer green spaces and recreational facilities',
      '• Access to healthcare and transportation varies by socioeconomic status',
      '• Housing quality and safety standards differ across income levels',
      '<br/>',
      '<strong>The Justice Perspective:</strong> Environmental inequalities are not just unfortunate – they\'re unfair. Everyone deserves access to surroundings that support their health and well-being, regardless of socioeconomic status.'
    ]
  },
  {
    title: 'My Space Audit Activity',
    content: [
      'Conduct a comprehensive audit of your personal environment to identify areas for improvement.',
      '<br/>',
      '<strong>Activity Steps:</strong>',
      '1. <strong>Select 5 Daily Environmental Aspects:</strong> Choose elements you interact with regularly (lighting, air quality, noise, cleanliness, safety, temperature, organization, etc.)',
      '2. <strong>Rate Each Aspect:</strong> Score from 1-5 (1 = major issue, 5 = excellent condition)',
      '3. <strong>Identify One Improvement:</strong> For each aspect, note one specific, actionable improvement',
      '4. <strong>Prioritize Changes:</strong> Rank improvements by impact and feasibility',
      '<br/>',
      '<strong>Example Audit:</strong>',
      '• <strong>Lighting:</strong> Rating 3/5 - Install better desk lamp (high impact, easy to implement)',
      '• <strong>Air Quality:</strong> Rating 2/5 - Add indoor plants and improve ventilation',
      '• <strong>Noise:</strong> Rating 4/5 - Use white noise machine for better sleep',
      '• <strong>Organization:</strong> Rating 2/5 - Declutter workspace and create storage solutions',
      '• <strong>Safety:</strong> Rating 5/5 - Well-maintained and secure environment',
      '<br/>',
      '<strong>Implementation Plan:</strong> Choose your highest-impact, easiest-to-implement improvement and commit to completing it within one week. Small changes compound into significant improvements.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 2: The Ecology of Daily Life! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about environmental stressors and daily life ecology.',
        'The quiz will help you review key concepts about how your surroundings affect your stress levels and well-being.'
      ]
    }
  }
];

const EnvironmentalModule2Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('environmentalM2');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/environmental-resources');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/environmental-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left environmental-panel"
        >
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">The Ecology of Daily Life</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Explore how everyday environmental conditions impact your health and well-being.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/environmental-module-2/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule2Page;

